import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, BadgeCheck, Users } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getTypeLabel, ENTITY_TYPE_COLORS } from '@/constants/entityTypes';
import { formatShortLocation } from '@/utils/location';
import { getEntityImageUrl, makeImageFallback } from '@/lib/entityMedia';

// Last-ditch stock photo when neither the operator uploaded an image nor
// the entity has a working website to screenshot. Kept identical to the
// original hard-coded value so seeded data still looks the same.
const STOCK_FALLBACK = 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=400';

const ExperienceCard = ({ entity, variant = 'default' }) => {
  const navigate = useNavigate();
  // Extract to a local — the visual-edits Babel plugin trips on repeated
  // JSX references to `entity.business_group` inside a && guard.
  const businessGroup = entity.business_group;

  const aspectRatios = {
    portrait: 'aspect-[3/4]',
    square: 'aspect-square',
    landscape: 'aspect-[4/3]'
  };
  
  // Random aspect ratio for masonry effect
  const randomAspect = ['portrait', 'square', 'landscape'][Math.floor(Math.random() * 3)];
  const aspectRatio = variant === 'masonry' ? aspectRatios[randomAspect] : aspectRatios.landscape;

  // Render the type chip with the brand color from the centralized constants
  const typeColor = ENTITY_TYPE_COLORS[entity.entity_type] || '#FF0000';
  const typeChipStyle = {
    backgroundColor: `${typeColor}26`, // ~15% alpha
    color: typeColor,
  };

  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl cursor-pointer"
      onClick={() => navigate(`/entity/${entity.entity_id}`)}
      data-testid={`experience-card-${entity.entity_id}`}
    >
      {/* Image */}
      <div className={`relative ${aspectRatio} overflow-hidden`}>
        <img
          src={getEntityImageUrl(entity, { width: 800 }) || STOCK_FALLBACK}
          alt={entity.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={makeImageFallback(entity, STOCK_FALLBACK)}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Type badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium"
          style={typeChipStyle}
        >
          {getTypeLabel(entity.entity_type)}
        </div>
        
        {/* Verified badge */}
        {entity.verified && (
          <div className="absolute top-3 right-3 bg-primary/20 backdrop-blur-sm p-1.5 rounded-full">
            <BadgeCheck className="h-4 w-4 text-primary" />
          </div>
        )}
        
        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white font-semibold text-lg mb-1 line-clamp-1">{entity.name}</h3>
          {businessGroup ? (
            <p
              className="text-white/70 text-xs mb-1 line-clamp-1"
              data-testid="card-business-group"
            >
              Part of {businessGroup}
            </p>
          ) : null}
          
          <div className="flex items-center gap-3 text-white/80 text-sm">
            {entity.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {formatShortLocation(entity)}
              </span>
            )}
            {entity.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                {entity.rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
        
        {/* Avatar for guides */}
        {entity.entity_type === 'outfitters_guides' && entity.image_url && (
          <div className="absolute bottom-3 right-3">
            <Avatar className="h-10 w-10 border-2 border-white shadow-lg">
              <AvatarImage src={entity.image_url} />
              <AvatarFallback>{entity.name?.[0]}</AvatarFallback>
            </Avatar>
          </div>
        )}
      </div>
      
      {/* Tags */}
      {entity.tags?.length > 0 && (
        <div className="p-3 pt-2 flex flex-wrap gap-1.5">
          {entity.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="tag text-xs">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExperienceCard;
